import { v4 as uuidv4 } from 'uuid';
import { ErrorName, PlayerError } from '../../errors';
import { debug } from '../../utils/logger';
import BaseEngine from '../BaseEngine';
const MP3_MIME_TYPE = 'audio/mpeg';
const METADATA_LENGTH_SPECIFIER_MULTIPLIER = 16;
const BACK_BUFFER_SEC = 60;
const formatToMimeTypeMap = {
    mp3: MP3_MIME_TYPE,
};
export default class IcyEngine extends BaseEngine {
    static name = 'icy';
    static get MediaSourceApi() {
        return window.ManagedMediaSource ?? window.MediaSource ?? null;
    }
    static browserSupportsFormat(format) {
        const MS = IcyEngine.MediaSourceApi;
        return Boolean(MS?.isTypeSupported?.(formatToMimeTypeMap[format ?? '']));
    }
    #fetchAbortController = null;
    #textDecoder = new TextDecoder();
    #sourceBuffer;
    #isStopped = false;
    #loadId = null;
    #didLoad = false;
    async load() {
        const MediaSourceApi = IcyEngine.MediaSourceApi;
        if (!MediaSourceApi)
            throw new Error('MediaSource unsupported');
        this.#isStopped = false;
        this.#loadId = uuidv4();
        this.#fetchAbortController = null;
        this._mediaSource = new MediaSourceApi();
        this._mediaSource.addEventListener('sourceopen', this.#onSourceOpen);
        await super.load();
        this.#didLoad = true;
    }
    destroy() {
        this.#isStopped = true;
        this.#loadId = null;
        this._mediaSource?.removeEventListener('sourceopen', this.#onSourceOpen);
        this.#sourceBuffer?.removeEventListener('error', this.#onSourceError);
        this.#abortFetch();
        this.#endMediaSourceStream();
        super.destroy();
    }
    #waitForUpdateEnd = () => new Promise((resolve) => this.#sourceBuffer?.addEventListener('updateend', () => resolve(), {
        once: true,
    }));
    async #appendAndTrim(chunk, currentTime) {
        this.#sourceBuffer?.appendBuffer(chunk);
        await this.#waitForUpdateEnd();
        if (this.#sourceBuffer?.buffered.length &&
            currentTime - this.#sourceBuffer.buffered.start(0) > BACK_BUFFER_SEC &&
            !this.#sourceBuffer.updating) {
            const cutOff = currentTime - BACK_BUFFER_SEC / 2;
            this.#sourceBuffer.remove(0, cutOff);
            await this.#waitForUpdateEnd();
        }
    }
    #abortFetch() {
        try {
            this.#fetchAbortController?.abort();
        }
        catch {
        }
    }
    async #endMediaSourceStream(reason) {
        if (this.#sourceBuffer?.updating) {
            await this.#waitForUpdateEnd();
        }
        if (this._mediaSource?.readyState === 'open') {
            this._mediaSource.endOfStream(reason);
        }
        this.#sourceBuffer = undefined;
    }
    async #fetchStream() {
        this.#fetchAbortController = new AbortController();
        const tryFetch = async (withIcy) => {
            const response = await fetch(this._options.src, {
                headers: withIcy ? { 'Icy-Metadata': '1' } : undefined,
                signal: this.#fetchAbortController?.signal,
            });
            if (!response.ok || !response.body) {
                throw new Error(`IcyEngine stream fetch failed. Icy headers: ${withIcy.toString()}, Status: ${response.status}`);
            }
            const icyMetaInt = response.headers.get('icy-metaint');
            if (withIcy && !icyMetaInt) {
                throw new Error('IcyEngine stream fetch returned without an accessible Icy-Metaint header. Possible CORS issue.');
            }
            return {
                reader: response.body.getReader(),
                icyMetaInt: Number(icyMetaInt ?? 0),
            };
        };
        const onError = (e) => {
            debug('Icy Engine Error:', e?.message);
            if (e?.name !== 'AbortError') {
                this.emit('error', PlayerError.createError(ErrorName.MetadataError, e?.message));
            }
        };
        try {
            return await tryFetch(true);
        }
        catch (e) {
            onError(e);
        }
        try {
            debug('Icy Engine fallback request without Icy headers');
            return await tryFetch(false);
        }
        catch (e) {
            onError(e);
            throw e;
        }
    }
    async #streamLoop() {
        const { reader, icyMetaInt } = (await this.#fetchStream()) || {};
        if (!reader) {
            return;
        }
        if (!icyMetaInt) {
            while (true) {
                const { done, value } = await reader.read();
                if (done || this.#isStopped)
                    break;
                await this.#appendAndTrim(value, this.getCurrentTime());
            }
            return;
        }
        let buf = new Uint8Array(0);
        while (!this.#isStopped) {
            const { done, value } = await reader.read();
            if (this._mediaSource.readyState === 'ended' || this.#isStopped) {
                break;
            }
            if (done) {
                this.#endMediaSourceStream();
                break;
            }
            const merged = new Uint8Array(buf.length + value.length);
            merged.set(buf);
            merged.set(value, buf.length);
            buf = merged;
            let pos = 0;
            while (buf.length - pos >= icyMetaInt + 1) {
                await this.#appendAndTrim(buf.subarray(pos, pos + icyMetaInt), this.getCurrentTime());
                pos += icyMetaInt;
                const lenByte = buf[pos];
                const metaLen = lenByte * METADATA_LENGTH_SPECIFIER_MULTIPLIER;
                if (buf.length - pos - 1 < metaLen) {
                    break;
                }
                pos += 1;
                if (!metaLen) {
                    continue;
                }
                try {
                    const rawMetadata = this.#textDecoder.decode(buf.subarray(pos, pos + metaLen));
                    if (!rawMetadata) {
                        throw new Error('Received empty metadata');
                    }
                    this.emit('metadata', { icy: rawMetadata });
                }
                catch (e) {
                    const message = `Error parsing ICY metadata: ${e?.message}`;
                    this.emit('error', PlayerError.createError(ErrorName.MetadataError, message));
                }
                pos += metaLen;
            }
            buf = buf.slice(pos);
        }
    }
    #onSourceOpen = async () => {
        const loadId = this.#loadId;
        this.#sourceBuffer = this._mediaSource.addSourceBuffer(MP3_MIME_TYPE);
        this.#sourceBuffer.mode = 'sequence';
        this.#sourceBuffer.addEventListener('error', this.#onSourceError);
        try {
            await this.#streamLoop();
        }
        catch (err) {
            const wasAborted = err?.name === 'AbortError';
            if (wasAborted || this.#isStopped || loadId !== this.#loadId) {
                return;
            }
            if (!this.#didLoad && this._mediaSource?.readyState === 'open') {
                this.#endMediaSourceStream('network');
                return;
            }
            this.emit('error', PlayerError.createError(ErrorName.PlaybackErrorNetwork, err.message));
        }
    };
    #onSourceError = () => this.destroy();
}
